import React, { useState, useRef } from 'react';
import './SchoolOrganisation.css';
import campusBg from '../../assets/campus-bg.jpg';
import CurriculumMap from '../CurriculumMap/CurriculumMap';

const PROGRAMMES = [
  {
    tag: 'SHORT-TERM PROGRAMME',
    title: '4–5 Month Programme',
    badge: 'Campus Visit',
    minStudents: 'Min 30-50 students',
    stats: [
      { value: '4–5', label: 'Months' },
      { value: '32 hrs', label: 'Total Training' },
      { value: '90 min/week', label: 'Duration / Week' },
    ],
    desc: 'A hands-on STEM program conducted in schools for Grades 3–9, covering Electronics, Embedded Systems, IoT, and Robotics through practical, project-based learning.',
    kitNote: 'ibots trainers bring all kits to your campus. Kits are used by students during the session and taken back after each class.',
    includes: [
      'Electronics & Circuit Building',
      'Arduino & Embedded Systems',
      'IoT & Smart Devices',
      'Robotics & Automation',
      'Project-Based Learning',
      'ibots Certificate for Each Student',
    ],
  },
  {
    tag: 'FULL YEAR PROGRAMME',
    title: '8–9 Month Programme',
    badge: 'Campus Visit',
    minStudents: 'Min 30-50 students',
    stats: [
      { value: '8–9', label: 'Months' },
      { value: '32 hrs', label: 'Total Training' },
      { value: '45 min/week', label: 'Duration / Week' },
    ],
    desc: 'An in-school STEM program for Grades 3–9, offering hands-on learning in electronics, embedded systems, IoT, and robotics through structured, activity-based sessions integrated with the school schedule.',
    kitNote: 'ibots trainers bring all kits to your campus. Kits are used by students during the session and taken back after each class.',
    includes: [
      'Full Curriculum across 3 Phases',
      'Electronics, IoT & Robotics',
      'Structured Weekly Sessions',
      'Activity-Based Learning',
      'Annual STEM Showcase',
      'ibots Certificate for Each Student',
    ],
  },
];

function SchoolOrganisation({ onClose }) {
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCurriculum, setShowCurriculum] = useState(null);
  const curriculumRef = useRef(null);
  const [formData, setFormData] = useState({
    schoolName: '', contactPerson: '', phone: '', email: '',
    city: '', studentCount: '', gradeRange: '', programme: '',
    customProgramme: '', preferredStart: '', message: '',
    source: '', customSource: '',
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!formData.schoolName || !formData.contactPerson || !formData.phone || !formData.email || !formData.source) {
      alert('Please fill in all required fields.');
      return;
    }
    try {
      await fetch('http://localhost:5000/api/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'school-campus-programme',
          institution: formData.schoolName,
          contactPerson: formData.contactPerson,
          phone: formData.phone,
          email: formData.email,
          city: formData.city,
          studentCount: formData.studentCount,
          gradeRange: formData.gradeRange,
          programme: formData.programme === 'other' ? formData.customProgramme : formData.programme,
          preferredStart: formData.preferredStart,
          message: formData.message,
          source: formData.source === 'Other' ? formData.customSource : formData.source,
        }),
      });
    } catch (err) { console.log('Your backend error:', err); }

    try {
      await fetch('http://localhost:5000/api/campus', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.contactPerson,
          type: 'School',
          phone: formData.phone,
          email: formData.email,
          institution: formData.schoolName,
          city: formData.city || '',
          studentCount: formData.studentCount || '',
          gradeRange: formData.gradeRange || '',
          programme: ({
            'short': '4–5 Month Programme',
            'full':  '8–9 Month Programme',
            'other': formData.customProgramme || 'Other',
          }[formData.programme] || formData.programme || ''),
          preferredStart: formData.preferredStart || '',
          requirements: formData.message || '',
          mode: 'Offline',
          formType: 'Campus Programme',
          source: formData.source === 'Other' ? formData.customSource : formData.source,
        }),
      });
    } catch (err) { console.log('Campus dashboard error:', err); }

    setSubmitted(true);
  };

  return (
    <div className="so__wrapper">
      <div className="so__inner">

        <button className="so__close" onClick={onClose}>✕</button>

        <div className="so__header">
          <div className="so__header-left">
            <h2 className="so__title">School Organisation Training</h2>
            <p className="so__subtitle">
              ibots trainers conduct on-campus, hands-on technology sessions
              integrated with the school curriculum. Students gain practical
              exposure to Electronics, IoT, Robotics, and emerging technologies
              through interactive learning and project-based activities within
              the school environment.
            </p>
          </div>
          <button className="so__trainers-btn" onClick={() => setShowEnquiry(true)}>
            Trainers Come to Your School
          </button>
        </div>

        <div className="so__info-box">
          <span className="so__info-icon">🚌</span>
          <div>
            <p className="so__info-title">ibots Trainers Visit Your School Campus</p>
            <p className="so__info-desc">
              Our certified trainers travel to your school with all equipment
              and kits. Training happens in your school lab or classroom —
              no student commute needed.
            </p>
          </div>
        </div>

        <div className="so__badges">
          {[
            { text: 'Ratio:  30:1 or 50:1' },
            { text: 'Mode: Offline (at your campus)' },
            { text: 'Certificates Provided' },
            { text: 'Curriculum Customised per School' },
          ].map((b, i) => (
            <span key={i} className="so__badge"><span>{b.icon}</span> {b.text}</span>
          ))}
        </div>

        <div className="so__equipment">
          <span className="so__equipment-icon">🔧</span>
          <div>
            <p className="so__equipment-title">EQUIPMENT & KIT ARRANGEMENT — CAMPUS PROGRAMME</p>
            <p className="so__equipment-desc">
              ibots trainers arrive at your school with all required kits and equipment. Kits are distributed
              to students at the start of each session for hands-on use and are{' '}
              <strong>collected back by the ibots trainer at the end of every session</strong>.
              Schools are not required to procure, manage, or stock any hardware.
              All equipment logistics are handled entirely by ibots.
            </p>
          </div>
        </div>

        <div className="so__section-title">Curriculum-Based Programme — Choose Duration</div>

        <div className="so__programmes">
          {PROGRAMMES.map((prog, i) => (
            <div key={i} className="so__card">
              <div className="so__card-header">
                <div>
                  <p className="so__card-tag">{prog.tag}</p>
                  <p className="so__card-title">{prog.title}</p>
                </div>
                <span className="so__card-badge">{prog.badge}</span>
              </div>
              <div className="so__card-body">
                <div className="so__card-min-students">👥 {prog.minStudents}</div>
                <div className="so__card-stats">
                  {prog.stats.map((s, j) => (
                    <div key={j} className="so__card-stat">
                      <span className="so__card-stat-value">{s.value}</span>
                      <span className="so__card-stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>
                <p className="so__card-desc">{prog.desc}</p>
                <div className="so__card-kit">{prog.kitNote}</div>
                <ul className="so__card-includes">
                  {prog.includes.map((item, j) => (
                    <li key={j}><span className="so__check">✓</span> {item}</li>
                  ))}
                </ul>
                <button className="so__card-btn" onClick={() => {
                  setShowCurriculum(i === 0 ? 'short' : 'full');
                  setTimeout(() => { curriculumRef.current?.scrollIntoView({ behavior: 'smooth' }); }, 100);
                }}>
                  More Details →
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="so__table-section">
          <h3 className="so__table-title"><span>🛡</span> What's Included</h3>
          <div className="so__table-wrap">
            <table className="so__table">
              <thead>
                <tr><th>Feature</th><th>4–5 Month</th><th>8–9 Month</th></tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Certified Trainers', short: '✓ Visit campus', full: '✓ Visit campus' },
                  { feature: 'Equipment & Kits', short: '✓ Brought by ibots trainer, returned after session', full: '✓ Brought by ibots trainer, returned after session' },
                  { feature: 'Curriculum', short: 'Electronics, Embedded Systems, IoT & Robotics', full: 'Electronics, Embedded Systems, IoT & Robotics' },
                  { feature: 'Assessments', short: 'End of programme', full: 'Periodic + reports (every 3 months)' },
                  { feature: 'Certificates', short: '✓ Per student', full: '✓ Per student' },
                  { feature: 'Trainer Ratio', short: '30:1 or 50:1', full: '30:1 or 50:1' },
                  { feature: 'Session Duration', short: '90 mins/week', full: '45 mins/week' },
                  { feature: 'Total Hours', short: '32 hrs', full: '32 hrs' },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="so__table-feature">{row.feature}</td>
                    <td>{row.short}</td>
                    <td>{row.full}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Enquiry Form */}
      {showEnquiry && (
        <div className="so__overlay" onClick={() => { setShowEnquiry(false); setSubmitted(false); }}>
          <div className="so__form-box" onClick={(e) => e.stopPropagation()}>
            <button className="so__form-close" onClick={() => { setShowEnquiry(false); setSubmitted(false); }}>✕</button>

            {submitted ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'400px', textAlign:'center', gap:'16px' }}>
                <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'#dcfce7', color:'#16a34a', fontSize:'28px', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900 }}>✓</div>
                <h2 style={{ color:'#0d2e6e', fontWeight:900, margin:0 }}>Thank You!</h2>
                <p style={{ color:'#64748b', margin:0, maxWidth:'300px', lineHeight:'1.6' }}>We have received your campus programme enquiry. Our team will contact you within <strong>24 hours</strong>.</p>
                <button className="so__form-submit" onClick={() => { setShowEnquiry(false); setSubmitted(false); }}>Close</button>
              </div>
            ) : (
              <>
                <h2 className="so__form-title">School Campus Visit — Enquiry Form</h2>
                <p className="so__form-subtitle">Fill in your details and our team will contact you within 24 hours to schedule a campus visit.</p>

                <div className="so__form">
                  <div className="so__form-row">
                    <div className="so__form-group">
                      <label className="so__form-label">School Name *</label>
                      <input type="text" name="schoolName" value={formData.schoolName} onChange={handleChange} placeholder="Enter school name" className="so__form-input" />
                    </div>
                    <div className="so__form-group">
                      <label className="so__form-label">Contact Person *</label>
                      <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} placeholder="Principal / Coordinator name" className="so__form-input" />
                    </div>
                  </div>

                  <div className="so__form-row">
                    <div className="so__form-group">
                      <label className="so__form-label">Phone Number *</label>
                      <input type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" className="so__form-input" />
                    </div>
                    <div className="so__form-group">
                      <label className="so__form-label">Email Address *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@school.com" className="so__form-input" />
                    </div>
                  </div>

                  <div className="so__form-row">
                    <div className="so__form-group">
                      <label className="so__form-label">City</label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. Coimbatore" className="so__form-input" />
                    </div>
                    <div className="so__form-group">
                      <label className="so__form-label">Approximate Student Count</label>
                      <input type="text" name="studentCount" value={formData.studentCount} onChange={handleChange} placeholder="e.g. 60 students" className="so__form-input" />
                    </div>
                  </div>

                  <div className="so__form-row">
                    <div className="so__form-group">
                      <label className="so__form-label">Grade Range</label>
                      <select name="gradeRange" value={formData.gradeRange} onChange={handleChange} className="so__form-input so__form-select">
                        <option value="">Select grades...</option>
                        <option value="Grade 3–5">Grade 3–5</option>
                        <option value="Grade 6–8">Grade 6–8</option>
                        <option value="Grade 3–9">Grade 3–9 (All)</option>
                        <option value="Grade 9">Grade 9 only</option>
                      </select>
                    </div>
                    <div className="so__form-group">
                      <label className="so__form-label">Preferred Start</label>
                      <input type="text" name="preferredStart" value={formData.preferredStart} onChange={handleChange} placeholder="e.g. June 2025" className="so__form-input" />
                    </div>
                  </div>

                  <div className="so__form-group so__form-group--full">
                    <label className="so__form-label">Preferred Programme</label>
                    <select name="programme" value={formData.programme} onChange={handleChange} className="so__form-input so__form-select">
                      <option value="">Select...</option>
                      <option value="short">4–5 Month Programme</option>
                      <option value="full">8–9 Month Programme</option>
                      <option value="other">Other (specify below)</option>
                    </select>
                    {formData.programme === 'other' && (
                      <input type="text" name="customProgramme" value={formData.customProgramme} onChange={handleChange} placeholder="Describe your requirement..." className="so__form-input so__form-input--mt" />
                    )}
                  </div>

                  <div className="so__form-group so__form-group--full">
                    <label className="so__form-label">Message / Requirements</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Any specific requirements, preferred schedule, or questions..." className="so__form-input so__form-textarea" />
                  </div>

                  <div className="so__form-group so__form-group--full">
                    <label className="so__form-label">How did you hear about ibots? *</label>
                    <select name="source" value={formData.source} onChange={handleChange} className="so__form-input so__form-select">
                      <option value="">Select...</option>
                      {['Friend / Family Referral','School / College Recommendation','Google Search','Social Media (Instagram / Facebook)','YouTube','LinkedIn','WhatsApp','Newspaper / Magazine','Event / Hackathon','ibots Website','Other'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {formData.source === 'Other' && (
                      <input type="text" name="customSource" value={formData.customSource} onChange={handleChange} placeholder="Please tell us how you found us..." className="so__form-input" style={{ marginTop:'8px' }} />
                    )}
                  </div>

                  <button className="so__form-submit" onClick={handleSubmit}>Submit Enquiry →</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="so__timeline-wrapper" style={{ backgroundImage: `url(${campusBg})` }}>
        <div className="so__timeline-overlay" />
        <div className="so__timeline-content">
          <p className="so__timeline-sup">CAMPUS TRAINING PROGRAMME</p>
          <h3 className="so__timeline-title">How It Works</h3>
          <div className="so__timeline">
            {[
              { step: '01', label: 'Enquiry & Consultation', desc: "Programme Discussion — Understanding institutional requirements and providing the right training consultation tailored to student needs.", side: 'left' },
              { step: '02', label: 'Planning & Scheduling', desc: "Structured Planning — Designing a well-organized training plan with finalized batch schedules and session timelines.", side: 'right' },
              { step: '03', label: 'Trainer Arrives with Kits', desc: "Equipment & Technical Support — Expert trainers visit the campus with complete STEM kits, tools, and required training materials.", side: 'left' },
              { step: '04', label: 'Hands-On STEM Learning', desc: "Practical Learning Sessions — Students engage in real-time practical sessions focused on experiential STEM education.", side: 'right' },
              { step: '05', label: 'Innovation & Project Building', desc: "Project Development — Students design, build, and test innovative working projects under expert guidance.", side: 'left' },
              { step: '06', label: 'Weekly Practice & Assessments', desc: "Continuous Training Support — Regular practice sessions, activities, and assessments ensure consistent skill development.", side: 'right' },
              { step: '07', label: 'Reports & Performance Tracking', desc: "Progress Evaluation — Detailed performance monitoring and assessment reports help track student improvement and outcomes.", side: 'left' },
              { step: '08', label: 'Certification & Programme Completion', desc: "Successful Completion — Participants receive completion certificates upon successfully finishing the training programme.", side: 'right' },
            ].map((item, i) => (
              <div key={i} className="so__timeline-row">
                {item.side === 'left' ? (
                  <div className="so__timeline-item so__timeline-item--left">
                    <p className="so__step-label">STEP {item.step}</p>
                    <div className="so__step-group">
                      <div className="so__step-btn">{item.label}</div>
                      <div className="so__step-tooltip">
                        <p className="so__step-tooltip-title">{item.label}</p>
                        <p className="so__step-tooltip-desc">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ) : <div className="so__timeline-item--empty" />}
                <div className="so__timeline-dot-col"><div className="so__timeline-dot" /></div>
                {item.side === 'right' ? (
                  <div className="so__timeline-item so__timeline-item--right">
                    <p className="so__step-label">STEP {item.step}</p>
                    <div className="so__step-group">
                      <div className="so__step-btn">{item.label}</div>
                      <div className="so__step-tooltip">
                        <p className="so__step-tooltip-title">{item.label}</p>
                        <p className="so__step-tooltip-desc">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ) : <div className="so__timeline-item--empty" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={curriculumRef}>
        {showCurriculum && (
          <CurriculumMap programme={showCurriculum} onClose={() => setShowCurriculum(null)} />
        )}
      </div>

      <div style={{ background:'linear-gradient(135deg, #0d2e6e, #1a3a8f)', borderRadius:'16px', padding:'40px 32px', margin:'32px 0 16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'24px' }}>
        <div>
          <p style={{ margin:'0 0 8px', fontSize:'11px', fontWeight:700, color:'rgba(255,255,255,0.6)', textTransform:'uppercase', letterSpacing:'1px' }}>Campus Training Programme</p>
          <h3 style={{ margin:'0 0 10px', fontSize:'24px', fontWeight:900, color:'#fff' }}>Ready to Bring ibots to Your School?</h3>
          <p style={{ margin:0, fontSize:'14px', color:'rgba(255,255,255,0.75)', maxWidth:'480px', lineHeight:'1.7' }}>
            Our certified trainers come to your campus with all kits and equipment. Hands-on STEM learning for Grades 3–9 — no commute, no procurement needed.
          </p>
        </div>
        <button onClick={() => { setSubmitted(false); setShowEnquiry(true); }}
          style={{ background:'#fff', color:'#0d2e6e', border:'none', borderRadius:'10px', padding:'14px 28px', fontSize:'14px', fontWeight:800, cursor:'pointer', whiteSpace:'nowrap', boxShadow:'0 4px 16px rgba(0,0,0,0.15)' }}>
          Enroll a Campus Programme →
        </button>
      </div>

    </div>
  );
}

export default SchoolOrganisation;
